import { useId } from 'react';

import { cn } from '@/shared/lib/cn';

import { usePortalSettings } from './portal';

/**
 * Orbita mark: a planet with a tilted orbit and a satellite, on a squircle filled with the accent gradient.
 * Colors follow the runtime accent (`--brand`), so a custom portal color restyles the logo too.
 */
export function OrbitaMark({ className, title }: { className?: string; title?: string }) {
  const id = useId().replace(/:/gu, '');
  return (
    <svg
      viewBox="0 0 32 32"
      className={cn('size-7 shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="2" y1="1" x2="30" y2="31" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: 'color-mix(in oklch, var(--brand), white 12%)' }} />
          <stop offset="1" style={{ stopColor: 'color-mix(in oklch, var(--brand), #c026d3 42%)' }} />
        </linearGradient>
        <clipPath id={`${id}-front`}>
          <rect x="0" y="16" width="32" height="16" />
        </clipPath>
      </defs>
      <rect width="32" height="32" rx="9" fill={`url(#${id}-bg)`} />
      <g transform="rotate(-26 16 16)">
        <ellipse
          cx="16"
          cy="16"
          rx="11.5"
          ry="4.6"
          fill="none"
          stroke="white"
          strokeOpacity="0.42"
          strokeWidth="1.6"
        />
      </g>
      <circle cx="16" cy="16" r="5.4" fill="white" />
      <g transform="rotate(-26 16 16)" clipPath={`url(#${id}-front)`}>
        <ellipse cx="16" cy="16" rx="11.5" ry="4.6" fill="none" stroke="white" strokeWidth="1.6" />
      </g>
      <circle cx="25.3" cy="10.9" r="2.1" fill="white" />
    </svg>
  );
}

/** The portal's square mark: the uploaded logo (Admin → portal settings) or the Orbita mark. */
export function PortalMark({ className }: { className?: string }) {
  const { data } = usePortalSettings();
  if (data?.logoUrl) {
    return (
      <img
        src={data.logoUrl}
        alt=""
        data-testid="portal-logo"
        className={cn('size-7 shrink-0 rounded-[9px] object-contain', className)}
      />
    );
  }
  return <OrbitaMark className={className} />;
}

/** Logo + portal name (the company name from the portal settings; "Orbita" until they load). */
export function OrbitaLogo({ className, markClassName }: { className?: string; markClassName?: string }) {
  const { data } = usePortalSettings();
  const name = data?.companyName ?? 'Orbita';
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2.5', className)}>
      {data?.logoUrl ? (
        <img
          src={data.logoUrl}
          alt=""
          data-testid="portal-logo"
          className={cn('h-7 w-auto max-w-[6rem] shrink-0 object-contain', markClassName)}
        />
      ) : (
        <OrbitaMark className={markClassName} />
      )}
      <span
        className="truncate text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground"
        data-testid="portal-name"
        title={name}
      >
        {name}
      </span>
    </span>
  );
}
